import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../components/StyledComponents";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import CustomSelect from "../../../components/CustomSelect";
import HolidayApi from "./../../../axios/LeaveAndHoliday/HolidayApi";
import MonthsData from "../../../assets/data/MonthData";
import DaysOfWeekData from "../../../assets/data/DaysOfWeekData";
import DaysOfMonthData from "../../../assets/data/DaysOfMonthData";
import { toast } from "react-toastify";
import { format, isBefore, startOfDay } from "date-fns";
import dayjs from "dayjs";
import { useAuth } from "../../../context/AuthContext";

const AddHolidayDialog = (props) => {
  const { t } = useTranslation();
  const {
    open,
    handleClose,
    setLoading,
    onRefresh,
    selectedDate,
  } = props;
  const { addHoliday } = HolidayApi();
  const { genaralSettingTimeZone } = useAuth();

  const customStartDate = new Date(format(selectedDate, "yyyy-MM-dd 00:00:00"));
  const customEndDate = new Date(format(selectedDate, "yyyy-MM-dd 23:59:00"));
  const [isInputValid, setIsInputValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recurringTypeData = useMemo(
    () => [
      { value: "weekly", label: t("common.weekly") },
      { value: "monthly", label: t("common.monthly") },
      { value: "yearly", label: t("common.yearly") },
    ],
    [t]
  );

  const monthData = MonthsData();
  const weekDayData = DaysOfWeekData();
  const monthDayData = DaysOfMonthData();

  const initialFormData = {
    isHolidayRecurring: false,
    recurringType: "weekly",
    day: "monday",
    month: "january",
    normalDateFrom: new Date(customStartDate),
    normalDateTo: new Date(customEndDate),
    startFrom: new Date(customStartDate),
    endsOn: new Date(customEndDate),
    description: "",
  };

  const toDate = (val) => {
    const v = val && val.target && val.target.value ? val.target.value : val;
    if (dayjs.isDayjs(v)) return v.toDate();
    if (typeof v === "string") return new Date(v);
    return new Date(v);
  };

  const getStartEnd = (fd) => {
    if (!fd.isHolidayRecurring) {
      return {
        start: toDate(fd.normalDateFrom),
        end: toDate(fd.normalDateTo),
      };
    }
    const s = startFrom ? toDate(fd.startFrom) : null;
    const e = endsOn ? toDate(fd.endsOn) : null;
    return { start: s, end: e };
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      normalDateFrom: new Date(customStartDate),
      normalDateTo: new Date(customEndDate),
      startFrom: new Date(customStartDate),
      endsOn: new Date(customEndDate),
    }));
  }, [selectedDate]);

  const [isDescriptionError, setIsDescriptionError] = useState(false);
  const [datepickerKey, setDatepickerKey] = useState(Date.now());
  const [validationMessages, setValidationMessages] = useState({
    description: "",
  });
  const [startFrom, setStartFrom] = useState(true);
  const [endsOn, setEndsOn] = useState(false);

  const handleChangeStartFrom = (event) => {
    setStartFrom(event.target.value === "true");
  };

  const handleChangeEndsOn = (event) => {
    setEndsOn(event.target.value === "true");
  };

  useEffect(() => {
    if (formData.recurringType === "weekly") {
      if (!weekDayData.some((option) => option.value === formData.day)) {
        setFormData((prev) => ({ ...prev, day: weekDayData[0].value }));
      }
    } else if (!monthDayData.some((option) => option.value === formData.day)) {
      setFormData((prev) => ({ ...prev, day: monthDayData[0].value }));
    }
  }, [formData.recurringType]);

  useEffect(() => {
    const { description } = formData;
    const isDescriptionValid =
      description !== "" && validationMessages.description === "";
    setIsDescriptionError(isDescriptionValid ? null : true);
  }, [formData.description, validationMessages.description]);

  const handleCloseDialog = () => {
    handleClose();
    setIsDescriptionError(null);
    setStartFrom(true);
    setEndsOn(false);
    setFormData({
      ...initialFormData,
      normalDateFrom: selectedDate,
      normalDateTo: selectedDate,
      startFrom: selectedDate,
      endsOn: selectedDate,
    });
    setDatepickerKey(Date.now());
  };

  const handleNormalDateRangeChange = ([start, end]) => {
    if (!start || !end) return;
    setFormData((prev) => ({
      ...prev,
      normalDateFrom: start,
      normalDateTo: end,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue;

    if (type === "radio") {
      newValue = value === "true";
    } else if (type === "checkbox") {
      newValue = e.target.checked;
    } else {
      newValue = value;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    if (name === "description") {
      if (value === "") {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          description: "Description is required",
        }));
      } else {
        setValidationMessages((prevMessages) => ({
          ...prevMessages,
          description: "",
        }));
      }
    }
  };

  const mapFormDataToApiData = (data) => {
    if (data.isHolidayRecurring === false) {
      return {
        isHolidayRecurring: false,
        startDate: toUTCStartOfDayByCompanyTZ(
          data.normalDateFrom?.target?.value || data.normalDateFrom,
          genaralSettingTimeZone
        ),
        endDate: toUTCEndOfDayByCompanyTZ(
          data.normalDateTo?.target?.value || data.normalDateTo,
          genaralSettingTimeZone
        ),
        description: data.description,
        recurringType: "",
        recurringDay: "",
        recurringMonth: "",
      };
    }

    if (data.recurringType === "weekly" || data.recurringType === "monthly") {
      return {
        isHolidayRecurring: data.isHolidayRecurring,
        startDate: toUTCStartOfDayByCompanyTZ(
          data.startFrom?.target?.value || data.startFrom,
          genaralSettingTimeZone
        ),
        endDate: toUTCEndOfDayByCompanyTZ(
          data.endsOn?.target?.value || data.endsOn,
          genaralSettingTimeZone
        ),
        description: data.description,
        recurringType: data.recurringType,
        recurringDay: data.day,
        recurringMonth: "",
      };
    }

    if (data.recurringType === "yearly") {
      return {
        isHolidayRecurring: data.isHolidayRecurring,
        startDate: toUTCStartOfDayByCompanyTZ(
          data.startFrom?.target?.value || data.startFrom,
          genaralSettingTimeZone
        ),
        endDate: toUTCEndOfDayByCompanyTZ(
          data.endsOn?.target?.value || data.endsOn,
          genaralSettingTimeZone
        ),
        description: data.description,
        recurringType: data.recurringType,
        recurringDay: data.day,
        recurringMonth: data.month,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData.isHolidayRecurring &&
      formData.recurringType === "yearly" &&
      !isYearlyDateInRange(formData)
    ) {
      toast.warning(t("common.recurring-start-end"));
      return;
    }

    setIsSubmitting(true);

    const submitData = mapFormDataToApiData(formData);

    try {
      // Chỉ áp dụng "no specified" cho holiday recurring; holiday thường luôn gửi startDate/endDate
      if (formData.isHolidayRecurring) {
        if (!startFrom) {
          submitData.startDate = null;
        }
        if (!endsOn) {
          submitData.endDate = null;
        }
      }
      submitData.langId = localStorage.getItem("i18nextLngId");

      const response = await addHoliday(submitData);
      if (response.data.responseCode === 0) {
        handleCloseDialog();
        toast.success(t("common.add-holiday-successfully"));
        onRefresh();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(error.response.data.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const { start, end } = getStartEnd(formData);

    if (start && end && isBefore(startOfDay(end), startOfDay(start))) {
      setValidationMessages((m) => ({
        ...m,
        endDate: t("common.invalid-date"),
      }));
      setIsInputValid(false);
    } else {
      setValidationMessages((m) => ({ ...m, endDate: "" }));
      setIsInputValid(true);
    }
  }, [
    formData.isHolidayRecurring,
    formData.normalDateFrom,
    formData.normalDateTo,
    formData.startFrom,
    formData.endsOn,
    startFrom,
    endsOn,
    t,
  ]);

  const normalizeOffset = (tz) => {
    if (!tz) return "+00:00";

    const cleaned = tz.trim();

    if (/^[+-]\d{2}:\d{2}$/.test(cleaned)) {
      return cleaned;
    }

    return "+00:00";
  };

  const toUTCStartOfDayByCompanyTZ = (date, tzOffset) => {
    if (!date) return null;

    const offset = normalizeOffset(tzOffset);

    return dayjs(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .utcOffset(offset, true)
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss[Z]");
  };

  const toUTCEndOfDayByCompanyTZ = (date, tzOffset) => {
    if (!date) return null;

    const offset = normalizeOffset(tzOffset);

    return dayjs(date)
      .hour(23)
      .minute(59)
      .second(59)
      .millisecond(0)
      .utcOffset(offset, true)
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss[Z]");
  };

  const isYearlyDateInRange = (data) => {
    if (data.recurringType !== "yearly") return true;
    if (!startFrom || !endsOn) return true;

    const start = toDate(data.startFrom);
    const end = toDate(data.endsOn);

    if (!start || !end) return true;

    const monthIndex = monthData.findIndex((m) => m.value === data.month);
    const day = Number(data.day);

    if (monthIndex === -1 || !day) return false;

    const holidayDate = new Date(start.getFullYear(), monthIndex, day);

    return (
      !isBefore(holidayDate, startOfDay(start)) &&
      !isBefore(startOfDay(end), startOfDay(holidayDate))
    );
  };

  const dayOptions =
    formData.recurringType === "weekly" ? weekDayData : monthDayData;

  return (
    <CustomDialog
      maxWidth={"sm"}
      open={open}
      loading={isSubmitting}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("common.add-holiday")}</p>
          </div>
          <GrayIconButton
            onClick={handleCloseDialog}
            className="self-start"
            disabled={isSubmitting}
          >
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="pt-5 flex flex-col gap-3">
          {/* Holiday type */}
          <div className="grid grid-cols-6 items-center">
            <div className="col-span-2 text-sm text-[#171717]">
              {t("common.holiday-type")}
            </div>
            <div className="col-span-4 flex justify-end">
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="isHolidayRecurring"
                  name="isHolidayRecurring"
                  value={formData.isHolidayRecurring}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value={false}
                    control={<Radio size="small" />}
                    label={t("common.normal")}
                  />
                  <FormControlLabel
                    sx={{ marginLeft: "50px", marginRight: "unset" }}
                    value={true}
                    control={<Radio size="small" />}
                    label={t("common.recurring")}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>

          {/* Normal holiday */}
          {formData.isHolidayRecurring === false && (
            <div>
              <label className="block text-sm text-[#171717] mb-2">
                {t("common.date")}
                <span className="text-[#EF4444] ml-1">*</span>
              </label>
              <CustomDateTimeRangePicker
                key={datepickerKey}
                mode="range"
                value={[formData.normalDateFrom, formData.normalDateTo]}
                disableTime
                showOnlyDate
                onChange={handleNormalDateRangeChange}
              />
              {validationMessages.endDate && (
                <p className="mt-1 text-xs text-[#DC2626]">
                  {validationMessages.endDate}
                </p>
              )}
            </div>
          )}

          {/* Recurring holiday */}
          {formData.isHolidayRecurring === true && (
            <>
              <div>
                <label className="block text-sm text-[#171717] mb-2">
                  {t("common.recurring-type")}
                  <span className="text-[#EF4444] ml-1">*</span>
                </label>
                <FormControl className="w-full">
                  <CustomSelect
                    name="recurringType"
                    id="recurringType"
                    value={formData.recurringType}
                    onChange={handleInputChange}
                    options={recurringTypeData}
                    dataType="recurringType"
                    defaultValue={
                      recurringTypeData.length > 0
                        ? recurringTypeData[0].value
                        : ""
                    }
                  />
                </FormControl>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#171717] mb-2">
                    {t("common.day")}
                    <span className="text-[#EF4444] ml-1">*</span>
                  </label>
                  <CustomSelect
                    name="day"
                    id="recurringDay"
                    value={formData.day}
                    onChange={handleInputChange}
                    options={dayOptions}
                    defaultValue={
                      dayOptions.length > 0 ? dayOptions[0].value : ""
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#171717] mb-2">
                    {t("common.month")}
                    <span className="text-[#EF4444] ml-1">*</span>
                  </label>
                  <CustomSelect
                    name="month"
                    id="recurringMonth"
                    value={formData.month}
                    onChange={handleInputChange}
                    options={monthData}
                    dataType="month"
                    readOnly={formData.recurringType !== "yearly"}
                    defaultValue={monthData[0].value}
                  />
                </div>
              </div>

              <div>
                <p className="text-sm text-[#171717] mb-2">{t("common.start")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="startFrom"
                      name="startFromRad"
                      value={startFrom}
                      onChange={handleChangeStartFrom}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" />}
                        label={t("common.no-specified")}
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio size="small" />}
                        label={t("common.start-from")}
                      />
                    </RadioGroup>
                  </FormControl>
                  <div
                    className={`flex-1 min-w-[160px] ${
                      !startFrom ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    <CustomDateTimeRangePicker
                      mode="single"
                      value={formData.startFrom}
                      disableTime
                      showOnlyDate
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "startFrom", value: newValue },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#171717] mb-2">{t("common.end")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="endsOn"
                      name="endsOnRad"
                      value={endsOn}
                      onChange={handleChangeEndsOn}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" />}
                        label={t("common.no-specified")}
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio size="small" />}
                        label={t("common.ends-on")}
                      />
                    </RadioGroup>
                  </FormControl>
                  <div
                    className={`flex-1 min-w-[160px] ${
                      !endsOn ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    <CustomDateTimeRangePicker
                      mode="single"
                      value={formData.endsOn}
                      disableTime
                      showOnlyDate
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "endsOn", value: newValue },
                        })
                      }
                    />
                  </div>
                </div>
                {validationMessages.endDate && (
                  <p className="mt-1 text-xs text-[#DC2626]">
                    {validationMessages.endDate}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm text-[#171717] mb-2">
              {t("common.description")}
              <span className="text-[#EF4444] ml-1">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t("common.enter-holiday-description")}
              rows={3}
              className="form-control w-full resize-y min-h-[80px] p-2"
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton
            onClick={handleCloseDialog}
            disabled={isSubmitting}
          >
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={isSubmitting || !isInputValid || isDescriptionError}
          >
            <p>{t("button.add")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddHolidayDialog;
